/*
 * Datos del índice ORIGINAL de fotos de monstruos (VANGUARD_MONSTRUOS_INDICE_COMPACTO.json).
 * - photoIndexNotes: lo que el staff transcribió en el chat (sin lámina ni recorte: NO cuenta como contrastado).
 * - Índice cargado: el staff puede cargar el JSON en el panel de verificación; se guarda en ESTE navegador y se aplica
 *   al recargar. Mientras el JSON no esté en el proyecto, los visitantes no lo ven.
 * Ninguna nota confirma la apariencia dentro de Vanguard. visually_checked es un antecedente del índice, no una
 * inspección hecha ahora.
 */
export type PhotoIndexNote = {
  sourceName?: string
  mappedName?: string
  sourcePage?: string
  matchMethod?: string
  familyReference?: boolean
  /** visually_checked del índice: antecedente de revisión de quien creó el índice. */
  indexVisuallyChecked?: boolean
  retouched?: boolean
  warning?: string
}

export type IndexRecord = PhotoIndexNote & { id: number; sheetRaw: string; sheetPack?: 'inicial' | 'extra'; sheetNumber?: number; rect?: [number, number, number, number]; sheetWidth?: number; sheetHeight?: number }

const MAYA = 'Denominación descriptiva documentada en el índice original; la diferencia de nombre no indica una imagen equivocada. No es una prueba dentro del servidor.'
const EGGS = 'family_reference=true en el índice original: referencia de familia, no apariencia exacta confirmada en Vanguard.'

export const photoIndexNotes: Record<number, PhotoIndexNote> = {
  362: { sourceName: 'Maya Hand', mappedName: 'Hand of Maya (left)', matchMethod: 'Nombre descriptivo (lado izquierdo)', warning: MAYA },
  363: { sourceName: 'Maya Hand', mappedName: 'Hand of Maya (right)', matchMethod: 'Nombre descriptivo (lado derecho)', warning: MAYA },
  460: { familyReference: true, warning: EGGS },
  461: { familyReference: true, warning: EGGS },
  462: { familyReference: true, warning: EGGS },
}

export const PHOTO_INDEX_STORAGE_KEY = 'vanguard.monsterPhotoIndex.v1'

// ---------- Lectura del JSON (tolerante con el formato) ----------
const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : typeof v === 'number' ? String(v) : undefined)
const bool = (v: unknown) => (typeof v === 'boolean' ? v : v === 'true' || v === 1 ? true : v === 'false' || v === 0 ? false : undefined)

function sheetSizes(json: Record<string, unknown>): Map<string, { w: number; h: number }> {
  const out = new Map<string, { w: number; h: number }>()
  const visit = (v: unknown, keyHint?: string) => {
    if (Array.isArray(v)) v.forEach((x) => visit(x))
    else if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>
      const name = str(o.file) ?? str(o.filename) ?? str(o.name) ?? str(o.sheet) ?? keyHint
      const w = Number(o.width ?? o.w)
      const h = Number(o.height ?? o.h)
      if (name && w > 0 && h > 0) out.set(name.toLowerCase(), { w, h })
      else Object.entries(o).forEach(([k, x]) => visit(x, k))
    }
  }
  visit(json.new_sheets)
  visit(json.previous_sheets_already_uploaded)
  visit(json.sheets)
  return out
}

function parseSheet(raw: string): { pack?: 'inicial' | 'extra'; n?: number } {
  const n = raw.match(/(\d+)(?!.*\d)/)?.[1]
  const pack = /extra/i.test(raw) ? 'extra' : /limpio|mobs/i.test(raw) ? 'inicial' : undefined
  return { pack, n: n ? Number(n) : undefined }
}

export function parsePhotoIndex(text: string): { records: IndexRecord[]; error?: string } {
  let json: Record<string, unknown>
  try {
    json = JSON.parse(text)
  } catch {
    return { records: [], error: 'El texto no es un JSON válido.' }
  }
  const monsters = json.monsters as Record<string, Record<string, unknown>> | undefined
  if (!monsters || typeof monsters !== 'object') return { records: [], error: 'El JSON no tiene la clave "monsters".' }
  const sizes = sheetSizes(json)
  const records: IndexRecord[] = []
  Object.entries(monsters).forEach(([key, r]) => {
    const id = Number(key)
    if (!Number.isInteger(id) || !r || typeof r !== 'object') return
    const sheetRaw = str(r.sheet) ?? ''
    const { pack, n } = parseSheet(sheetRaw)
    const rect = Array.isArray(r.rect) && r.rect.length === 4 && r.rect.every((x) => typeof x === 'number') ? (r.rect as [number, number, number, number]) : undefined
    const size = sizes.get(sheetRaw.toLowerCase()) ?? Array.from(sizes.entries()).find(([k]) => sheetRaw && k.includes(sheetRaw.toLowerCase()))?.[1]
    records.push({
      id,
      sheetRaw,
      sheetPack: pack,
      sheetNumber: n,
      rect,
      sheetWidth: size?.w,
      sheetHeight: size?.h,
      sourceName: str(r.source_name),
      sourcePage: str(r.source_page),
      matchMethod: str(r.match_method),
      familyReference: bool(r.family_reference),
      indexVisuallyChecked: bool(r.visually_checked),
      retouched: bool(r.retouched),
      mappedName: str(r.mapped_name) ?? str(r.catalog_name),
      warning: str(r.warning) ?? str(r.warnings) ?? str(r.note) ?? str(r.notes),
    })
  })
  return { records: records.sort((a, b) => a.id - b.id) }
}

// ---------- Índice guardado en este navegador ----------
let loaded: Map<number, IndexRecord> | undefined

function readStored(): Map<number, IndexRecord> | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const text = window.localStorage.getItem(PHOTO_INDEX_STORAGE_KEY)
    if (!text) return undefined
    const { records } = parsePhotoIndex(text)
    return records.length ? new Map(records.map((r) => [r.id, r])) : undefined
  } catch {
    return undefined
  }
}

export function loadedPhotoIndex(): Map<number, IndexRecord> | undefined {
  if (loaded === undefined) loaded = readStored() ?? new Map()
  return loaded.size ? loaded : undefined
}

export function storePhotoIndex(text: string): void {
  window.localStorage.setItem(PHOTO_INDEX_STORAGE_KEY, text)
  loaded = undefined
}

export function clearStoredPhotoIndex(): void {
  window.localStorage.removeItem(PHOTO_INDEX_STORAGE_KEY)
  loaded = undefined
}

/** Nota del índice: transcripción del staff + registro del JSON cargado (si existe). */
export const photoIndexNote = (id: number): PhotoIndexNote | undefined => {
  const t = photoIndexNotes[id]
  const r = loadedPhotoIndex()?.get(id)
  if (!t && !r) return undefined
  return {
    ...r,
    ...t,
    familyReference: t?.familyReference || r?.familyReference,
    warning: [t?.warning, r?.warning].filter(Boolean).join(' · ') || undefined,
  }
}
